import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { authAtom, authStsAtom, userAtom, roleAtom, outletAtom, orgAtom, subAtom } from '../core/config/atoms';

import Lottie from 'lottie-react';
import { toast } from 'react-toastify';
import { jwtDecode } from "jwt-decode";

import ServiceAuth from 'services/serviceAuth';
import ServiceUtil from 'services/serviceUtil';
import ServiceOutlet from 'services/serviceOutlet';

import animBtnLoading from '../assets/anim/anim-btnLoading.json';
import bgLogin from '../assets/image/login.png';
import { ArrowRight } from 'lucide-react';

export default function PageAuth() {

    const [authStatus, setAuthStatus] = useRecoilState(authAtom);
    const [authSts, setAuthSts] = useRecoilState(authStsAtom);
    const [, setUserData] = useRecoilState(userAtom);
    const [, setRoleData] = useRecoilState(roleAtom);
    const [, setOrgData] = useRecoilState(orgAtom);
    const [, setOutletData] = useRecoilState(outletAtom);
    const [, setSubData] = useRecoilState(subAtom);

    const [orgs, setOrgs] = useState([]);
    const [outlets, setOutlets] = useState([]);

    const [submit, setSubmit] = useState(false);

    const emailRef = useRef('');
    const passwordRef = useRef('');

    const navigate = useNavigate();
    const authService = new ServiceAuth();
    const outletService = new ServiceOutlet();
    const utilService = new ServiceUtil();

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmit(true);

        const email = emailRef.current.value;
        const password = passwordRef.current.value;

        if (/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)) {
            if (password === "" || password === undefined) {
                toast.error('Password should not be empty !', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                setSubmit(false);
            } else {
                var body = {
                    email: email,
                    password: password
                }
                authService.userLogin(body, '').then((res) => {
                    if (res.status) {
                        setAuthStatus(true);
                        setAuthSts("org");
                        setUserData(res.data);
                        utilService.saveEncrypted("user", res.data);
                        setSubmit(false);
                        loadOrgs();
                    } else {
                        toast.error('Error: ' + res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                        setSubmit(false);
                    }
                });
            }
        } else {
            toast.error('Enter valid email id !', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            setSubmit(false);
        }
    }

    const setOrg = (id) => {
        (async function () {
            let existingCookies = await utilService.getStoredCookies();
            existingCookies = Object.assign(existingCookies, {
                'env': 'prod',
                'tenant-id': id,
            });
            setOrgData(id);
            await utilService.saveCookies(existingCookies);
            setAuthSts("outlet");
            loadOutlet();
        })();
    }

    const setOutlet = (outl) => {
        (async function () {
            setOutletData(outl);
            var body = { "outlet": outl["outletid"]["id"] };
            var res = await authService.userRABL(body);
            var rablToken = res["data"];
            const decoded = jwtDecode(rablToken);
            setRoleData(decoded);
            utilService.saveEncrypted("roles", decoded);
            var subRes = await outletService.getSubByFilter({ 'subid': outl["outletid"]["subid"] });
            setSubData(subRes['data']);
            authService.userRefresh().then((userRes) => {
                setUserData(userRes.data);
                utilService.saveEncrypted("user", userRes.data);
                setAuthSts("verified");
                if (userRes.data.modules.includes("TERMINAL")) {
                    navigate('/pos');
                } else {
                    navigate('/reception');
                }
            });
        })();
    }

    const loadOrgs = () => {
        authService.userOrg({}, '').then((res) => {
            setOrgs(res["data"]);
            if (res["data"].length == 1) {
                setOrgData(res["data"][0]['ordid']);
                setAuthSts("outlet");
                loadOutlet();
            }
        });
    }

    const loadOutlet = () => {
        authService.userOutlet({}, '').then((res) => {
            setOutlets(res["data"]);
            if (res["data"].length == 1) {
                var body = { "outlet": res["data"][0]["outletid"]["id"] };
                setOutletData(res["data"][0]);
                authService.userRABL(body, '').then((resRABL) => {
                    var rablToken = resRABL["data"];
                    const decoded = jwtDecode(rablToken);
                    setRoleData(decoded);
                    utilService.saveEncrypted("roles", decoded);
                    outletService.getSubByFilter({ 'subid': res["data"][0]['outletid']['subid'] }).then((subRes) => {
                        setSubData(subRes['data']);
                        authService.userRefresh().then((userRes) => {
                            setUserData(userRes.data);
                            utilService.saveEncrypted("user", userRes.data);
                            setAuthSts("verified");
                            if (decoded.modules.includes("TERMINAL")) {
                                navigate('/pos');
                            } else {
                                navigate('/reception');
                            }
                        });
                    });
                });
            }
        });
    }

    useEffect(() => {
        if (authSts === "verified") {
            navigate('/pos');
        } else if (authSts === "org") {
            loadOrgs();
        } else if (authSts === "outlet") {
            loadOutlet();
        }
    }, [authStatus, navigate]);

    return (
        <>
            <div className="w-screen h-screen overflow-hidden relative">
                <div className="xl:grid xl:grid-cols-12 xl:gap-0">
                    <div className="absolute xl:relative w-full xl:col-span-5 z-10">
                        <div className="h-screen flex flex-col items-center justify-center xl:bg-blue-50">
                            {
                                authSts === "open" && <div className="shadow-lg rounded-xl px-4 xl:px-10 py-20 xl:py-10 bg-white mt-6 mx-2 max-w-md md:w-auth w-11/12">
                                    <h1 className="text-2xl text-black font-serif font-medium">Welcome back!</h1>
                                    <h2 className="text-sm text-gray-400 font-serif">We`re so excited to see you again!</h2>
                                    <form className="mt-8" onSubmit={handleSubmit}>
                                        <div className="flex flex-col">
                                            <label className="text-sm text-gray-900 font-serif">Email ID
                                                <sup className="text-red-600">*</sup>
                                            </label>
                                            <input type="email" placeholder="i.e. jhon.deo@gmail.com" className="w-full h-11 rounded bg-blue-50 px-4 py-2 text-sm font-sans mt-1" required autoComplete="off" ref={emailRef} />
                                        </div>
                                        <div className="flex flex-col mt-4">
                                            <label className="text-sm text-gray-900 font-serif">Password
                                                <sup className="text-red-600">*</sup>
                                            </label>
                                            <input type="password" placeholder="i.e. xxxxxxxx" className="w-full h-11 rounded bg-blue-50 px-4 py-2 text-sm font-sans mt-1" required autoComplete="off" ref={passwordRef} />
                                        </div>
                                        <button type="submit" className="mt-8 text-xs font-bold text-white rounded bg-prime w-full h-10 font-mono flex items-center justify-center" disabled={submit}>
                                            {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" />}
                                            {!submit && "SUBMIT"}
                                        </button>
                                        <p className='text-sm mt-4 text-center w-[240px] mx-auto'>By clicking continue, you agree to our <b><u>Terms of Service</u></b> and <b><u>Privacy Policy</u></b>.</p>
                                    </form>
                                    <div id="captcha"></div>
                                    <div className="flex-grow"></div>
                                    <p className="text-sm text-gray-400 font-serif text-center mt-16">&copy; 2025 MPJ Digital Kisaan E-Bazaar PVT LTD.</p>
                                </div>
                            }
                            {
                                authSts === "org" && <div className="shadow-lg rounded-xl px-4 xl:px-10 py-20 xl:py-10 bg-white mt-6 mx-2 max-w-md md:w-auth w-11/12">
                                    <h1 className="text-2xl text-black font-serif font-medium">Select Organization!</h1>
                                    <h2 className="text-sm text-gray-400 font-serif">Please select organization you want to access!</h2>
                                    <div className='my-2'>
                                        {
                                            orgs.map((org) => {
                                                return <div className='border border-gray-400 rounded-md flex items-center p-2' key={org["id"]} onClick={() => { setOrg(org["ordid"]); }}>
                                                    <div className='w-[54px] h-[54px] flex items-center justify-center border border-prime rounded-full'>
                                                        <h3 className='text-xl font-medium'>{org["ordid"][0]}</h3>
                                                    </div>
                                                    <div className='flex flex-col items-start ml-2 leading-2'>
                                                        <h3 className='text-lg font-medium'>{org["ordid"]}</h3>
                                                        <p className='text-sm'>{org["tradNme"]}</p>
                                                    </div>
                                                    <div className='flex-grow'></div>
                                                    <ArrowRight size={22} />
                                                </div>
                                            })
                                        }
                                    </div>
                                    <div className="flex-grow"></div>
                                    <p className="text-sm text-gray-400 font-serif text-center mt-16">&copy; 2025 MPJ Digital Kisaan E-Bazaar PVT LTD.</p>
                                </div>
                            }
                            {
                                authSts === "outlet" && <div className="shadow-lg rounded-xl px-4 xl:px-10 py-20 xl:py-10 bg-white mt-6 mx-2 max-w-md md:w-auth w-11/12">
                                    <h1 className="text-2xl text-black font-serif font-medium">Select Outlet!</h1>
                                    <h2 className="text-sm text-gray-400 font-serif">Please select outlet you want to access!</h2>
                                    <div className='my-2'>
                                        {
                                            outlets.map((outlet) => {
                                                return <div className='border border-gray-400 rounded-md flex items-center p-2' key={outlet["outletid"]["id"]} onClick={() => { setOutlet(outlet); }}>
                                                    <div className='w-[54px] h-[54px] flex items-center justify-center border border-prime rounded-full'>
                                                        <h3 className='text-xl font-medium'>{outlet["outletid"]["name"][0]}</h3>
                                                    </div>
                                                    <div className='flex flex-col items-start ml-2 leading-2'>
                                                        <h3 className='text-lg font-medium'>{outlet["outletid"]["outletid"]}</h3>
                                                        <p className='text-sm'>{outlet["outletid"]["name"]}</p>
                                                    </div>
                                                    <div className='flex-grow'></div>
                                                    <ArrowRight size={22} />
                                                </div>
                                            })
                                        }
                                    </div>
                                    <div className="flex-grow"></div>
                                    <p className="text-sm text-gray-400 font-serif text-center mt-16">&copy; 2025 MPJ Digital Kisaan E-Bazaar PVT LTD.</p>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="h-screen xl:col-span-7 xl:flex items-center bg-prime">
                        <div className="w-full bg-pattern bg-[length:80%_80px] bg-repeat bg-center h-screen flex items-start lg:items-center justify-center relative p-[100px] overflow-hidden z-0">
                            <img src={bgLogin} alt="brand logo" className="w-[188px] h-full object-contain" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}